import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { downloadCSV } from "@/lib/erpdocs";
import { waLink, mailtoLink } from "@/lib/erp";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Search, Wallet, Copy, Link2, Landmark, Download, MessageCircle, Mail, UploadCloud, FileText, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [rowsError, setRowsError] = useState("");
  const [customersError, setCustomersError] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", invoice_id: "", amount: 0, payment_mode: "Cash", reference: "", bank: "", notes: "" });
  const location = useLocation();

  const load = useCallback(async () => {
    try { const r = await http.get("/payments", { params: { search } }); setRows(r.data); setRowsError(""); }
    catch (e) { setRowsError(requestErrorMessage(e, "load payments")); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const loadCustomers = useCallback(async () => {
    try { const r = await http.get("/customers"); setCustomers(r.data); setCustomersError(""); }
    catch (e) { setCustomersError(requestErrorMessage(e, "load customers")); }
  }, []);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  const [company, setCompany] = useState({});
  const [linkAmt, setLinkAmt] = useState("");
  const [linkNote, setLinkNote] = useState("");
  const [stmts, setStmts] = useState([]);
  const [companyError, setCompanyError] = useState("");
  const [stmtsError, setStmtsError] = useState("");
  const [invoicesError, setInvoicesError] = useState("");
  const fileRef = React.useRef(null);
  const loadCompany = useCallback(async () => {
    try { const r = await http.get("/company"); setCompany(r.data); setCompanyError(""); }
    catch (e) { setCompanyError(requestErrorMessage(e, "load payment settings")); }
  }, []);
  const loadStmts = useCallback(async () => {
    try { const r = await http.get("/bank-statements"); setStmts(r.data); setStmtsError(""); }
    catch (e) { setStmtsError(requestErrorMessage(e, "load bank statements")); }
  }, []);
  useEffect(() => { loadCompany(); loadStmts(); }, [loadCompany, loadStmts]);

  const uploadStmt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) return toast.error("Only PDF files allowed");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await http.post("/bank-statements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Statement uploaded"); loadStmts();
    } catch (err) { toast.error(requestErrorMessage(err, "upload bank statement")); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const viewStmt = async (s) => {
    try {
      const r = await http.get(`/bank-statements/${s.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(requestErrorMessage(e, "open bank statement")); }
  };
  const delStmt = async (id) => {
    if (!window.confirm("Delete statement?")) return;
    try { await http.delete(`/bank-statements/${id}`); toast.success("Deleted"); loadStmts(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete bank statement")); }
  };
  const upi = company.upi_id || "";
  const upiLink = `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(company.name || "")}&am=${linkAmt || ""}&cu=INR&tn=${encodeURIComponent(linkNote || "")}`;
  const copyLink = async () => {
    try {
      if (!navigator.clipboard) throw new Error("Clipboard access is unavailable");
      await navigator.clipboard.writeText(upiLink);
      toast.success("Payment link copied");
    } catch (e) { toast.warning(requestErrorMessage(e, "copy payment link")); }
  };
  const exportStatement = () => downloadCSV("bank_statement.csv", ["Date", "Receipt", "Customer", "Mode", "Credit"],
    rows.map((p) => [p.payment_date, p.payment_no, p.customer_name, p.payment_mode, p.amount]));

  const receiptMsg = (p) => `Dear ${p.customer_name},\n\nWe acknowledge receipt ${p.payment_no} for ₹${Number(p.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} via ${p.payment_mode}${p.invoice_no ? ` against ${p.invoice_no}` : ""}.\n\nThank you.`;
  const shareReceiptWA = (p) => { const c = customers.find((x) => x.id === p.customer_id); if (!c?.phone) return toast.error("No phone saved for this customer"); window.open(waLink(c.phone, receiptMsg(p)), "_blank"); };
  const shareReceiptEmail = (p) => { const c = customers.find((x) => x.id === p.customer_id); if (!c?.email) return toast.error("No email saved for this customer"); window.location.href = mailtoLink(c.email, `Receipt ${p.payment_no}`, receiptMsg(p)); };

  useEffect(() => {
    const cid = new URLSearchParams(location.search).get("customer");
    if (cid) { setForm((f) => ({ ...f, customer_id: cid })); setOpen(true); }
  }, [location.search]);

  const loadInvoices = useCallback(async () => {
    if (!form.customer_id) return;
    setInvoices([]);
    try {
      const r = await http.get("/invoices", { params: { customer_id: form.customer_id } });
      setInvoices(r.data.filter((i) => i.balance_due > 0 && i.status !== "cancelled"));
      setInvoicesError("");
    } catch (e) { setInvoicesError(requestErrorMessage(e, "load customer invoices")); }
  }, [form.customer_id]);
  useEffect(() => {
    if (form.customer_id) {
      setInvoicesError("");
      loadInvoices();
    } else { setInvoices([]); setInvoicesError(""); }
  }, [form.customer_id, loadInvoices]);

  const openNew = () => { setForm({ customer_id: "", invoice_id: "", amount: 0, payment_mode: "Cash", reference: "", bank: "", notes: "" }); setOpen(true); };

  const save = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
    try {
      const payload = { ...form, invoice_id: form.invoice_id || null };
      await http.post("/payments", payload);
      toast.success("Payment recorded"); setOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, "record payment")); }
  };

  const pickInvoice = (id) => {
    const inv = invoices.find((i) => i.id === id);
    setForm({ ...form, invoice_id: id, amount: inv ? inv.balance_due : form.amount });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Payments / Receipts</h1>
          <p className="text-sm text-muted-foreground">{rows.length} receipts</p>
        </div>
        <Button onClick={openNew} data-testid="record-payment-button"><Plus className="h-4 w-4 mr-1" /> Record Payment</Button>
      </div>
      {rowsError && <div role="alert" className="flex items-center justify-between gap-3 border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-700"><span>{rowsError}</span><Button size="sm" variant="outline" onClick={load}>Retry payments</Button></div>}
      {customersError && <div role="status" className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-800"><span>{customersError}; customer selection is unavailable.</span><Button size="sm" variant="outline" onClick={loadCustomers}>Retry customers</Button></div>}

      <Tabs defaultValue="receipts">
        <TabsList className="flex flex-wrap h-auto justify-start">
          <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-pay-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-payment-links">Payment Links</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-bank-statements">Bank Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="receipts" className="mt-4">
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search receipt, customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="payment-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Receipt No</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Invoice</th>
                <th className="px-4 py-3 font-medium">Mode</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium text-right no-print">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono font-medium">{p.payment_no}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.payment_date}</td>
                  <td className="px-4 py-3">{p.customer_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.invoice_no || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{p.payment_mode}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600 font-semibold">{inr(p.amount)}</td>
                  <td className="px-4 py-3 no-print"><div className="flex justify-end gap-1">
                    <button onClick={() => shareReceiptWA(p)} className="p-1.5 hover:bg-emerald-50 rounded" data-testid={`pay-wa-${p.id}`}><MessageCircle className="h-4 w-4 text-emerald-600" /></button>
                    <button onClick={() => shareReceiptEmail(p)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`pay-email-${p.id}`}><Mail className="h-4 w-4 text-brand-600" /></button>
                  </div></td>
                </tr>
              ))}
              {!rowsError && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />No payments</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card className="p-5">
            {rows.length === 0 ? <div className="py-12 text-center text-muted-foreground"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />No payments yet</div> : (
              <div className="relative pl-6 border-l-2 border-brand-200 space-y-5">
                {rows.map((p) => (
                  <div key={p.id} className="relative" data-testid={`pay-timeline-${p.id}`}>
                    <span className="absolute -left-[27px] top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-medium text-sm">{p.customer_name} <span className="text-xs bg-slate-100 px-2 py-0.5 rounded ml-1">{p.payment_mode}</span></div>
                        <div className="text-xs text-muted-foreground">{p.payment_date} · {p.payment_no}{p.invoice_no ? ` · ${p.invoice_no}` : ""}</div>
                      </div>
                      <div className="font-mono font-semibold text-emerald-600">+{inr(p.amount)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          <Card className="p-6 max-w-xl space-y-4">
            <div className="flex items-center gap-2 font-heading font-semibold"><Link2 className="h-5 w-5 text-brand-600" /> UPI Payment Link</div>
            {companyError && <div role="status" className="flex items-center justify-between gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3"><span>{companyError}</span><Button size="sm" variant="outline" onClick={loadCompany}>Retry</Button></div>}
            {upi ? <>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Amount (₹)</Label><Input type="number" value={linkAmt} onChange={(e) => setLinkAmt(e.target.value)} className="mt-1.5" data-testid="link-amount-input" placeholder="Optional" /></div>
                <div><Label>Note</Label><Input value={linkNote} onChange={(e) => setLinkNote(e.target.value)} className="mt-1.5" placeholder="e.g. INV-001" /></div>
              </div>
              <div><Label>Pay to UPI</Label><div className="mt-1.5 font-mono text-sm bg-slate-50 border rounded px-3 py-2">{upi}</div></div>
              <div className="break-all text-xs bg-brand-50 border border-brand-200 rounded px-3 py-2 font-mono" data-testid="upi-link-text">{upiLink}</div>
              <div className="flex items-center gap-4 flex-wrap">
                <Button onClick={copyLink} data-testid="copy-link-button"><Copy className="h-4 w-4 mr-1" /> Copy Payment Link</Button>
                <div className="flex flex-col items-center gap-1 p-3 bg-white border rounded-lg" data-testid="upi-qr">
                  <QRCodeCanvas value={upiLink} size={140} level="M" includeMargin />
                  <span className="text-[11px] text-muted-foreground">Scan to pay via any UPI app</span>
                </div>
              </div>
            </> : <div className="text-sm text-muted-foreground">Set a UPI ID in Settings → Banks & Payments to generate payment links.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="statement" className="mt-4 space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 font-heading font-semibold"><UploadCloud className="h-5 w-5 text-brand-600" /> Uploaded Bank Statements (PDF)</div>
              <div>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={uploadStmt} data-testid="statement-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-statement-button"><UploadCloud className="h-4 w-4 mr-1" /> Upload PDF</Button>
              </div>
            </div>
            {stmtsError && <div role="status" className="mt-3 flex items-center justify-between gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3"><span>{stmtsError}</span><Button size="sm" variant="outline" onClick={loadStmts}>Retry</Button></div>}
            {!stmtsError && stmts.length === 0 ? (
              <div className="mt-3 text-sm text-muted-foreground">No statements uploaded yet. Upload a bank statement PDF to keep it on file.</div>
            ) : !stmtsError && (
              <div className="mt-3 divide-y">
                {stmts.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5" data-testid={`stmt-row-${s.id}`}>
                    <FileText className="h-4 w-4 text-brand-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{s.label}</div>
                      <div className="text-xs text-muted-foreground">{s.original_name} · {(s.size / 1024).toFixed(0)} KB · {(s.uploaded_at || "").slice(0, 10)}</div>
                    </div>
                    <button onClick={() => viewStmt(s)} className="p-1.5 hover:bg-slate-100 rounded" title="View" data-testid={`view-stmt-${s.id}`}><Eye className="h-4 w-4 text-slate-600" /></button>
                    <button onClick={() => delStmt(s.id)} className="p-1.5 hover:bg-rose-50 rounded" title="Delete" data-testid={`del-stmt-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-semibold"><Landmark className="h-5 w-5 text-brand-600" /> Bank Statement · Money In <span className="text-sm text-muted-foreground font-normal">(total {inr(rows.reduce((s, p) => s + Number(p.amount || 0), 0))})</span></div>
            <Button size="sm" variant="outline" onClick={exportStatement} disabled={!rows.length} data-testid="export-statement"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </div>
          <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Receipt</th><th className="px-4 py-3 font-medium">Customer</th><th className="px-4 py-3 font-medium">Mode</th><th className="px-4 py-3 font-medium text-right">Credit</th><th className="px-4 py-3 font-medium text-right">Balance</th></tr></thead>
            <tbody>{(() => { let bal = 0; return [...rows].reverse().map((p) => { bal += Number(p.amount || 0); return (
              <tr key={p.id} className="border-t" data-testid={`statement-row-${p.id}`}><td className="px-4 py-3">{p.payment_date}</td><td className="px-4 py-3 font-mono text-xs">{p.payment_no}</td><td className="px-4 py-3">{p.customer_name}</td><td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{p.payment_mode}</span></td><td className="px-4 py-3 text-right font-mono text-emerald-600">+{inr(p.amount)}</td><td className="px-4 py-3 text-right font-mono font-semibold">{inr(bal)}</td></tr>
            ); }); })()}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground"><Landmark className="h-8 w-8 mx-auto mb-2 opacity-40" />No entries</td></tr>}</tbody>
          </table></div></Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, invoice_id: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="payment-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {customersError && <p className="mt-1 text-xs text-amber-700">{customersError}</p>}
            </div>
            <div>
              <Label>Against Invoice (optional)</Label>
              <Select value={form.invoice_id} onValueChange={pickInvoice} disabled={!form.customer_id}>
                <SelectTrigger className="mt-1.5" data-testid="payment-invoice-select"><SelectValue placeholder="On-account / select invoice" /></SelectTrigger>
                <SelectContent>
                  {!invoicesError && invoices.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">No pending invoices</div>}
                  {invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no} — bal {inr(i.balance_due)}</SelectItem>)}
                </SelectContent>
              </Select>
              {invoicesError && <div role="status" className="mt-1 flex items-center justify-between gap-2 text-xs text-amber-700"><span>{invoicesError}</span><Button size="sm" variant="outline" onClick={loadInvoices}>Retry</Button></div>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: +e.target.value })} className="mt-1.5" data-testid="payment-amount-input" /></div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Cash", "Bank", "UPI", "Cheque", "Card", "NEFT", "RTGS", "IMPS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Reference No</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Bank</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-payment-button">Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
